import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import moment from "moment-jalaali";
import {
  HiOutlineChartBar,
  HiOutlineChartPie,
  HiOutlineTrendingUp,
  HiTrendingUp,
  HiOutlineSparkles,
} from "react-icons/hi";
import { HiOutlineArrowPath } from "react-icons/hi2";
import Chart from "chart.js/auto";
import { useTranslation } from "react-i18next";
import type { AdminDashboardReport } from "types/reportTypes";

interface AdminChartProps {
  data?: AdminDashboardReport;
  technologicSalesData?: AdminDashboardReport;
  title: string;
  description?: string;
  isLoading: boolean;
  onRefetch?: (params?: { fromDate?: string; toDate?: string }) => void;
  type: "sales" | "export" | "capacity" | "ohi";
  className?: string;
  fromDate?: string;
  toDate?: string;
  selectedSectorId?: string | number;
}

const AdminChart: React.FC<AdminChartProps> = ({
  data,
  technologicSalesData,
  title,
  description,
  isLoading,
  onRefetch,
  type,
  className = "",
  fromDate,
  toDate,
  selectedSectorId = "all",
}) => {
  const { t, i18n } = useTranslation();
  const isPersian = i18n.language === "fa";
  const lineChartCanvasRef = useRef<HTMLCanvasElement>(null);
  const lineChartInstanceRef = useRef<Chart | null>(null);
  const [showTechnologicSales, setShowTechnologicSales] = useState(false);

  const lastFetchRef = useRef<{
    fromDate?: string;
    toDate?: string;
    sectorId?: string | number;
  } | null>(null);
  const hasMountedRef = useRef(false);

  const effectiveSectorId = selectedSectorId ?? "all";

  const getCurrentYearISO = useCallback(() => {
    const now = new Date();
    if (isPersian) {
      const persianDate = moment(now);
      const year = persianDate.jYear();
      return getPersianYearStartISO(year);
    }
    return getGregorianYearStartISO(now.getFullYear());
  }, [isPersian]);

  const getCurrentYearEndISO = useCallback(() => {
    const now = new Date();
    if (isPersian) {
      const persianDate = moment(now);
      const year = persianDate.jYear();
      return getPersianYearEndISO(year);
    }
    return getGregorianYearEndISO(now.getFullYear());
  }, [isPersian]);

  const getPersianYearStartISO = (persianYear: number): string => {
    const date = moment(`${persianYear}-01-01`, "jYYYY-jMM-jDD");
    return date.startOf("day").utc().toISOString();
  };

  const getPersianYearEndISO = (persianYear: number): string => {
    const isLeap = moment(`${persianYear}-12-30`, "jYYYY-jMM-jDD").isValid();
    const day = isLeap ? 30 : 29;
    const date = moment(`${persianYear}-12-${day}`, "jYYYY-jMM-jDD");
    return date.endOf("day").utc().toISOString();
  };

  const getGregorianYearStartISO = (year: number): string => {
    const date = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
    return date.toISOString();
  };

  const getGregorianYearEndISO = (year: number): string => {
    const date = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
    return date.toISOString();
  };

  const extractYearFromISO = useCallback(
    (isoString: string): number => {
      const date = new Date(isoString);
      if (isPersian) {
        const persianDate = moment(date);
        return persianDate.jYear();
      }
      return date.getFullYear();
    },
    [isPersian]
  );

  const getYearNumber = useCallback(
    (isoString: string): string => {
      if (isPersian) {
        const year = extractYearFromISO(isoString);
        return String(year);
      }
      const date = new Date(isoString);
      return String(date.getFullYear());
    },
    [isPersian, extractYearFromISO]
  );

  const getReportYearKey = (isoString: string): string =>
    String(new Date(isoString).getUTCFullYear());

  const defaultFromDate = fromDate || getCurrentYearISO();
  const defaultToDate = toDate || getCurrentYearEndISO();

  // جلوگیری از API call های تکراری
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      lastFetchRef.current = { fromDate, toDate, sectorId: effectiveSectorId };
      return;
    }

    const currentKey = `${fromDate}-${toDate}-${effectiveSectorId}`;
    const lastKey = `${lastFetchRef.current?.fromDate}-${lastFetchRef.current?.toDate}-${lastFetchRef.current?.sectorId}`;

    if (currentKey !== lastKey && !isLoading && data) {
      lastFetchRef.current = { fromDate, toDate, sectorId: effectiveSectorId };
      onRefetch?.({ fromDate, toDate });
    }
  }, [fromDate, toDate, effectiveSectorId, onRefetch, isLoading, data]);

  const processedData = useMemo(() => {
    if (!data) return null;

    const sectors = data.industrySectors || [];
    const showBySector = effectiveSectorId !== "all";

    const selectedSector = sectors.find(
      (sector) => String(sector.id) === String(effectiveSectorId)
    );

    let allYears: string[] = [];

    if (fromDate && toDate) {
      const fromYear = extractYearFromISO(fromDate);
      const toYear = extractYearFromISO(toDate);

      for (let year = fromYear; year <= toYear; year++) {
        if (isPersian) {
          allYears.push(getPersianYearStartISO(year));
        } else {
          allYears.push(getGregorianYearStartISO(year));
        }
      }
    } else {
      if (showBySector && selectedSector) {
        const sectorYears = Object.keys(selectedSector.years || {}).sort();
        allYears = sectorYears.map((year) => {
          if (isPersian) {
            return getPersianYearStartISO(parseInt(year) - 621);
          }
          return getGregorianYearStartISO(parseInt(year));
        });
      } else {
        const dataYears = Object.keys(data.years || {}).sort();
        allYears = dataYears.map((year) => {
          if (isPersian) {
            return getPersianYearStartISO(parseInt(year) - 621);
          }
          return getGregorianYearStartISO(parseInt(year));
        });
      }
    }

    if (allYears.length === 0) {
      allYears = [defaultFromDate];
    }

    const hasMultipleYears = allYears.length > 1;

    const isCardMode =
      !hasMultipleYears && (type === "sales" || type === "export");
    const isPieChart = !hasMultipleYears && !isCardMode;
    const isLineMode =
      hasMultipleYears || (type === "sales" && showTechnologicSales);

    const getReportValue = (
      report: AdminDashboardReport | undefined,
      yearISO: string,
      sectorId: string | number
    ) => {
      if (!report) return 0;
      const yearKey = getReportYearKey(yearISO);

      if (sectorId !== "all") {
        const sector = report.industrySectors?.find(
          (item) => String(item.id) === String(sectorId)
        );
        return sector?.years?.[yearKey] || 0;
      }

      return report.years?.[yearKey] || 0;
    };

    const prepareTrendData = () => {
      return allYears.map((isoYear: string) => {
        const value = getReportValue(data, isoYear, effectiveSectorId);
        const technologicValue = getReportValue(
          technologicSalesData,
          isoYear,
          effectiveSectorId
        );

        return {
          year: getYearNumber(isoYear),
          value: value,
          technologicValue: showTechnologicSales ? technologicValue : 0,
          rawYear: isoYear,
        };
      });
    };

    const preparePieData = () => {
      const isoYear = allYears[0] || defaultFromDate;
      const value = getReportValue(data, isoYear, effectiveSectorId);

      return [
        {
          name:
            showBySector && selectedSector
              ? selectedSector.name
              : t("dashboard.charts.totalRecords"),
          value,
          rawYear: isoYear,
        },
      ];
    };

    const prepareCardData = () => {
      const isoYear = allYears[0] || defaultFromDate;
      const displayYear = getYearNumber(isoYear);
      const value = getReportValue(data, isoYear, effectiveSectorId);
      const technologicValue = getReportValue(
        technologicSalesData,
        isoYear,
        effectiveSectorId
      );

      return {
        year: displayYear,
        value,
        technologicValue: showTechnologicSales ? technologicValue : 0,
        sectors: showBySector ? [selectedSector] : sectors,
      };
    };

    let totalValue = data.total || 0;
    if (showBySector && selectedSector) {
      totalValue = selectedSector.total || 0;
    }

    const hasAnyData = allYears.some((isoYear) => {
      return getReportValue(data, isoYear, effectiveSectorId) >= 0;
    });

    return {
      years: allYears,
      displayYears: allYears.map((iso) => getYearNumber(iso)),
      sectors,
      selectedSector,
      showBySector,
      hasMultipleYears,
      isPieChart,
      isCardMode,
      isLineMode,
      pieData: preparePieData(),
      trendData: isLineMode ? prepareTrendData() : [],
      cardData: isCardMode ? prepareCardData() : null,
      total: totalValue,
      hasData: hasAnyData,
    };
  }, [
    data,
    technologicSalesData,
    effectiveSectorId,
    type,
    showTechnologicSales,
    fromDate,
    toDate,
    isPersian,
    defaultFromDate,
    extractYearFromISO,
    getYearNumber,
    t,
  ]);

  useEffect(() => {
    lineChartInstanceRef.current?.destroy();
    lineChartInstanceRef.current = null;

    const canvas = lineChartCanvasRef.current;
    if (
      !canvas ||
      !processedData?.isLineMode ||
      !processedData.trendData.length
    ) {
      return;
    }

    const isDark = document.documentElement.classList.contains("dark");
    const lineColor = "#6366F1";
    const technologicSalesColor = "#10B981";
    const applicationFont = window.getComputedStyle(document.body).fontFamily;

    const datasets: any[] = [
      {
        label: title,
        data: processedData.trendData.map((item) => item.value),
        borderColor: lineColor,
        borderWidth: 3,
        tension: 0.42,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: lineColor,
        pointBorderWidth: 3,
        pointHoverBackgroundColor: lineColor,
        pointHoverBorderColor: "#FFFFFF",
        backgroundColor: (context: any) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;
          if (!chartArea) return "rgba(99, 102, 241, 0.15)";

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, "rgba(99, 102, 241, 0.38)");
          gradient.addColorStop(0.6, "rgba(139, 92, 246, 0.12)");
          gradient.addColorStop(1, "rgba(139, 92, 246, 0)");
          return gradient;
        },
      },
    ];

    if (showTechnologicSales && technologicSalesData) {
      datasets.push({
        label: t("dashboard.charts.technologicSales"),
        data: processedData.trendData.map((item) => item.technologicValue),
        borderColor: technologicSalesColor,
        borderWidth: 3,
        tension: 0.42,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: technologicSalesColor,
        pointBorderWidth: 3,
        pointHoverBackgroundColor: technologicSalesColor,
        pointHoverBorderColor: "#FFFFFF",
      });
    }

    lineChartInstanceRef.current = new Chart(canvas, {
      type: "line",
      data: {
        labels: processedData.trendData.map((item) => item.year),
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        font: { family: applicationFont },
        animation: {
          duration: 1100,
          easing: "easeOutQuart",
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              padding: 15,
              color: isDark ? "#D1D5DB" : "#374151",
            },
          },
          tooltip: {
            displayColors: true,
            padding: 12,
            backgroundColor: isDark ? "#1F2937" : "#111827",
            titleColor: "#FFFFFF",
            bodyColor: "#E0E7FF",
            cornerRadius: 10,
            titleFont: { family: applicationFont },
            bodyFont: { family: applicationFont },
            callbacks: {
              label: (context) =>
                `${context.dataset.label}: ${Number(
                  context.parsed.y
                ).toLocaleString()}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: isDark ? "#9CA3AF" : "#6B7280",
              font: { family: applicationFont, size: 12, weight: 600 },
            },
          },
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: isDark ? "#374151" : "#E5E7EB" },
            ticks: {
              color: isDark ? "#9CA3AF" : "#6B7280",
              font: { family: applicationFont, size: 12 },
              callback: (value) => Number(value).toLocaleString(),
            },
          },
        },
      },
    });

    return () => {
      lineChartInstanceRef.current?.destroy();
      lineChartInstanceRef.current = null;
    };
  }, [processedData, showTechnologicSales, technologicSalesData, t, title]);

  const renderPieChart = () => {
    if (!processedData?.pieData.length) {
      return (
        <div className="flex h-64 items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">
            {t("dashboard.charts.noData")}
          </p>
        </div>
      );
    }

    const item = processedData.pieData[0];
    const percentage = Math.min(100, Math.max(0, Number(item.value) || 0));
    const isZero = percentage === 0;
    const ringBackground = isZero
      ? "conic-gradient(#D1D5DB 0deg 360deg)"
      : `conic-gradient(
          from -90deg,
          #EF4444 0%,
          #F59E0B ${percentage / 2}%,
          #166534 ${percentage}%,
          #E5E7EB ${percentage}% 100%
        )`;

    return (
      <div className="flex h-72 w-full flex-col items-center justify-center gap-4">
        <div
          className="relative flex h-52 w-52 items-center justify-center rounded-full p-3 shadow-lg shadow-indigo-500/10 dark:shadow-none"
          style={{ background: ringBackground }}
          aria-label={`${item.name}: ${percentage.toLocaleString()}%`}
        >
          <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-white text-center dark:bg-gray-800">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              {percentage.toLocaleString()}%
            </span>
            <span className="mt-1 max-w-[130px] text-xs font-medium text-gray-500 dark:text-gray-400">
              {item.name}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <span
            className={`h-2.5 w-2.5 rounded-full ${
              isZero ? "bg-gray-300 dark:bg-gray-600" : "bg-indigo-500"
            }`}
          />
          <span>{item.name}</span>
        </div>
      </div>
    );
  };

  const renderTrendChart = () => {
    if (!processedData?.trendData.length) {
      return (
        <div className="flex h-64 items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">
            {t("dashboard.charts.noData")}
          </p>
        </div>
      );
    }

    return (
      <div className="to-transparent relative h-72 w-full overflow-hidden rounded-xl bg-gradient-to-b from-indigo-50/50 p-2 dark:from-indigo-500/5">
        <canvas ref={lineChartCanvasRef} />
      </div>
    );
  };

  const renderCard = () => {
    if (!data || !processedData?.cardData) return null;

    const { year, value, technologicValue } = processedData.cardData;
    const showBySector = processedData.showBySector;

    const sectorName = showBySector
      ? (data.industrySectors || []).find(
          (s) => String(s.id) === String(effectiveSectorId)
        )?.name || ""
      : "";

    return (
      <div className="space-y-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 p-6 text-white shadow-xl shadow-indigo-500/25"
        >
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />

          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/80">
                  {showBySector
                    ? `${t(
                        "dashboard.charts.totalFor"
                      )} ${year} - ${sectorName}`
                    : `${t("dashboard.charts.totalFor")} ${year}`}
                </p>
                <p className="mt-2 text-2xl font-bold">
                  {value.toLocaleString()} {t("actions.suffix")}
                </p>
                <p className="mt-1 text-sm text-white/60">
                  {type === "sales"
                    ? t("dashboard.charts.totalSales")
                    : t("dashboard.charts.totalExport")}
                </p>
              </div>

              {showTechnologicSales && technologicSalesData && (
                <div className="text-right">
                  <p className="text-sm font-medium text-white/80">
                    {t("dashboard.charts.technologicSales")}
                  </p>
                  <p className="text-emerald-300 mt-2 text-2xl font-bold">
                    {technologicValue.toLocaleString()} {t("actions.suffix")}
                  </p>
                  <p className="mt-1 text-sm text-white/60">
                    {showBySector ? sectorName : t("dashboard.charts.total")}
                  </p>
                </div>
              )}

              <div className="rounded-full bg-white/20 p-3 backdrop-blur-sm">
                {type === "sales" ? (
                  <HiTrendingUp className="h-8 w-8" />
                ) : (
                  <HiOutlineChartBar className="h-8 w-8" />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  const renderNoData = () => {
    return (
      <div className="flex h-80 flex-col items-center justify-center rounded-2xl bg-white shadow-xl dark:bg-gray-800/90">
        <div className="relative">
          <div className="rounded-full bg-gray-100 p-5 dark:bg-gray-700">
            <HiOutlineChartBar className="h-12 w-12 text-gray-400 dark:text-gray-500" />
          </div>
          <div className="absolute -right-2 -top-2">
            <HiOutlineSparkles className="h-6 w-6 text-indigo-400" />
          </div>
        </div>
        <h4 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
          {t("dashboard.charts.noData")}
        </h4>
        <p className="mt-1 max-w-sm text-center text-sm text-gray-500 dark:text-gray-400">
          {t("dashboard.charts.noDataDescription")}
        </p>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-80 items-center justify-center rounded-2xl bg-white shadow-xl dark:bg-gray-800/90">
        <div className="flex flex-col items-center gap-3">
          <div className="loader w-6 text-brand-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("dashboard.charts.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (!data || !processedData) {
    return renderNoData();
  }

  const {
    isPieChart,
    isCardMode,
    isLineMode,
    years = [],
    displayYears = [],
    showBySector = false,
    selectedSector = null,
  } = processedData;

  if (!years.length) {
    return renderNoData();
  }

  let chartType = "trend";
  if (isPieChart && !isCardMode) chartType = "pie";
  else if (isCardMode) chartType = "card";
  else if (isLineMode) chartType = "trend";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", damping: 20, stiffness: 300 }}
      className={`relative overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-800/90 ${className}`}
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

      <div className="border-b border-gray-200 p-6 dark:border-gray-700">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="animate-pulse absolute inset-0 rounded-xl bg-indigo-500/20 blur-lg" />
              <div className="relative rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-2.5 text-white shadow-lg shadow-indigo-500/25">
                {type === "sales" && <HiTrendingUp className="h-6 w-6" />}
                {type === "export" && <HiOutlineChartBar className="h-6 w-6" />}
                {type === "capacity" && (
                  <HiOutlineChartPie className="h-6 w-6" />
                )}
                {type === "ohi" && <HiOutlineTrendingUp className="h-6 w-6" />}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {title}
              </h3>
              {description && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {description}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {type === "sales" && (
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={showTechnologicSales}
                  onChange={(event) =>
                    setShowTechnologicSales(event.target.checked)
                  }
                  className="text-emerald-600 focus:ring-emerald-500 h-4 w-4 rounded border-gray-300"
                />
                {t("dashboard.charts.showTechnologicSales")}
              </label>
            )}

            <div
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                chartType === "pie"
                  ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300"
                  : chartType === "card"
                  ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"
                  : "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300"
              }`}
            >
              {chartType === "pie" &&
                "📊 " + t("dashboard.charts.distribution")}
              {chartType === "card" && "💰 " + t("dashboard.charts.summary")}
              {chartType === "trend" && "📈 " + t("dashboard.charts.trend")}
            </div>

            {displayYears.length > 0 && (
              <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {displayYears.length > 1
                  ? `${displayYears[0]} - ${
                      displayYears[displayYears.length - 1]
                    } (${displayYears.length})`
                  : `${displayYears[0]}`}
              </div>
            )}

            {showBySector && selectedSector && (
              <div className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
                🏭 {selectedSector.name}
              </div>
            )}

            {onRefetch && (
              <motion.button
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.5 }}
                onClick={() => {
                  onRefetch({
                    fromDate: fromDate || defaultFromDate,
                    toDate: toDate || defaultToDate,
                  });
                }}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                title={t("buttons.refresh")}
              >
                <HiOutlineArrowPath className="h-5 w-5" />
              </motion.button>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        {chartType === "pie" && renderPieChart()}
        {chartType === "card" && renderCard()}
        {chartType === "trend" && renderTrendChart()}
      </div>

      <div className="border-t border-gray-200 bg-gray-50/50 px-6 py-3 dark:border-gray-700 dark:bg-gray-800/50">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-indigo-500" />
              {t("dashboard.charts.totalRecords")}:{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {processedData?.total?.toLocaleString() || 0}
              </span>
            </span>

            {showTechnologicSales && technologicSalesData && (
              <span className="flex items-center gap-1.5">
                <span className="bg-emerald-500 inline-block h-2 w-2 rounded-full" />
                {t("dashboard.charts.technologicSales")}:{" "}
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {technologicSalesData?.total?.toLocaleString() || 0}
                </span>
              </span>
            )}
          </div>
          <span>
            {t("dashboard.charts.lastUpdated")}:{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {new Date().toLocaleString("fa-IR")}
            </span>
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminChart;
